<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DoubleMaterialityIroImpactType,
  DoubleMaterialityIroRiskAndOpportunityType,
} from '@/__generated__/types';
import useFormatNumber from '@/utils/composables/useFormatNumber';
import { BadgeType } from '@/components/atoms/AtBadge/types';
import AtBadge from '@/components/atoms/AtBadge/AtBadge.vue';
import { useStore } from '../../store';
import OgTable from '../../OgTable.vue';
import {finalMaterialityHeaders, getFinalMaterialityData} from './dataGenerators';
import type { FinalMaterialityRowItem } from '@/components/pages/Modules/DoubleMateriality/PgResults/PgList/types';

const { t } = useI18n();
const store = useStore();
const { formatNumber } = useFormatNumber();

const headers = computed(() =>
  finalMaterialityHeaders.map((h) => ({ ...h, text: t(h.text) })),
);

const items = computed<FinalMaterialityRowItem[]>(() => getFinalMaterialityData(store.value));
</script>

<template>
  <h3 class="text-lg font-medium mb-3">
    {{ t('Final materiality table') }}
  </h3>
  <OgTable
    :headers="headers"
    :items="items"
    :isRowRemovable="false"
    tableClass="!mb-16"
  >
    <template #item-esrs="row: FinalMaterialityRowItem">
      <p>{{ row.topic.esrs }} {{ t(row.topic.topic) }}</p>
    </template>
    <template #item-sustainabilityMatter="row: FinalMaterialityRowItem">
      <p>
        {{ t(row.topic.subSubTopic) || t(row.topic.subTopic) || '' }}
      </p>
    </template>
    <template #item-iro="row: FinalMaterialityRowItem">
      <p>
        {{
          row.iroImpact?.impact ??
          row.iroRiskAndOpportunity?.riskAndOpportunity ??
          ''
        }}
      </p>
    </template>
    <template #item-iroType="row: FinalMaterialityRowItem">
      <p>
        <template
          v-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.ACTUAL_NEGATIVE
          "
        >
          {{ t('Actual negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_NEGATIVE
          "
        >
          {{ t('Potential negative impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.ACTUAL_POSITIVE
          "
        >
          {{ t('Actual positive impact') }}
        </template>
        <template
          v-else-if="
            row.iroImpact?.type ===
            DoubleMaterialityIroImpactType.POTENTIAL_POSITIVE
          "
        >
          {{ t('Potential positive impact') }}
        </template>
        <template
          v-else-if="
            row.iroRiskAndOpportunity?.type ===
            DoubleMaterialityIroRiskAndOpportunityType.OPPORTUNITY
          "
        >
          {{ t('Opportunity') }}
        </template>
        <template
          v-else-if="
            row.iroRiskAndOpportunity?.type ===
            DoubleMaterialityIroRiskAndOpportunityType.RISK
          "
        >
          {{ t('Risk') }}
        </template>
        <template v-else>
          {{ t('n/a') }}
        </template>
      </p>
    </template>
    <template #item-internal="row: FinalMaterialityRowItem">
      <p>
        {{
          typeof row.internal !== 'number'
            ? t('n/a')
            : formatNumber(row.internal, 0, 2)
        }}
      </p>
    </template>
    <template #item-stakeholders="row: FinalMaterialityRowItem">
      <p>
        {{
          typeof row.stakeholders !== 'number'
            ? t('n/a')
            : formatNumber(row.stakeholders, 0, 2)
        }}
      </p>
    </template>
    <template #item-final="row: FinalMaterialityRowItem">
      <p>
        {{
          typeof row.final !== 'number'
            ? t('n/a')
            : formatNumber(row.final, 0, 2)
        }}
      </p>
    </template>
    <template #item-materiality="row: FinalMaterialityRowItem">
      <AtBadge
        :type="
          row.materiality === true
            ? BadgeType.SUCCESS
            : row.materiality === false
              ? BadgeType.WARNING
              : BadgeType.DEFAULT
        "
      >
        {{
          row.materiality === true
            ? t('Material')
            : row.materiality === false
              ? t('Not material')
              : t('Start')
        }}
      </AtBadge>
    </template>
  </OgTable>
</template>
